import type { FC } from 'react';
import { useDndSensors } from '@ContractBuilder/modules/shared/drag-and-drop';
import { DndContext } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import { getSortedEntries } from '@root/helpers';

import { useNavigation } from '../context/use-navigation';
import { useDragHandlers } from '../hooks/use-drag-handlers';
import { NavEndorsementDetailsController } from '../modules/nav-endorsement-details/NavEndorsementDetailsController';
import { NavSectionController } from '../modules/nav-section';
import { isEndorsementDetailsNavItem } from '../types';

import { NavigationDragOverlay } from './NavigationDragOverlay';

export const NavigationSectionsList: FC = () => {
  const { navItems, sections, activeId, activeType, activeSectionId, getShouldDisableOperations, isDragDisabled } =
    useNavigation();
  const sensors = useDndSensors();

  const { handleDragStart, handleDragOver, handleDragEnd } = useDragHandlers();

  // Which section currently holds the active block in the live preview?
  // Used to force-render closed sections so the SortableBlockWrapper stays mounted during drag.
  const activeBlockSectionId =
    activeId && activeType === 'block' ? sections.find((s) => s.blocks.some((b) => b.id === activeId))?.id : undefined;

  return (
    <DndContext onDragEnd={handleDragEnd} onDragOver={handleDragOver} onDragStart={handleDragStart} sensors={sensors}>
      <SortableContext items={navItems.map((s) => s.id)} id="sections-list-sortable-context">
        <ol>
          {navItems.map((item) => {
            if (isEndorsementDetailsNavItem(item)) {
              return <NavEndorsementDetailsController key={item.id} item={item} isDragDisabled={isDragDisabled} />;
            }

            const shouldDisableOperations = getShouldDisableOperations(item);
            const sortedBlocks = getSortedEntries(item.layout, item.blocks);
            const blockIds = sortedBlocks.map((b) => b.id);

            return (
              <SortableContext items={blockIds} key={item.id} id={`section-${item.id}-sortable-context`}>
                <NavSectionController
                  activeSection={activeSectionId}
                  forceOpen={activeBlockSectionId === item.id}
                  isDragDisabled={isDragDisabled}
                  section={item}
                  shouldDisableOperations={shouldDisableOperations}
                />
              </SortableContext>
            );
          })}
        </ol>
      </SortableContext>
      <NavigationDragOverlay />
    </DndContext>
  );
};
